import { Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { cn } from '@/shared/lib/cn';

export interface ApartmentTab {
  to: string;
  label: string;
}

interface Props {
  propertyId: string;
  tabs: ApartmentTab[];
  activeTo: string | undefined;
  /** Ändert sich der Pfad, wird der aktive Reiter wieder ins Bild geholt. */
  currentPath: string;
}

const TAB_MASK =
  'linear-gradient(to right, transparent 0, black 32px, black calc(100% - 32px), transparent 100%)';

/**
 * Die Reiterleiste der Apartmentakte. Sie darf als einzige Fläche horizontal scrollen (§9) —
 * deshalb holt sie den aktiven Reiter beim Routenwechsel selbst wieder ins Bild.
 */
export function ApartmentTabBar({ propertyId, tabs, activeTo, currentPath }: Props) {
  const activeTabRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
  }, [currentPath]);

  return (
    <nav
      role="tablist"
      aria-label="Apartment-Bereiche"
      className="scrollbar-hide -mx-8 mb-6 overflow-x-auto px-8 [&::-webkit-scrollbar]:hidden"
      style={{ scrollbarWidth: 'none' }}
    >
      <div
        className="flex min-w-max items-center gap-1.5"
        style={{ maskImage: TAB_MASK, WebkitMaskImage: TAB_MASK }}
      >
        {tabs.map((tab) => {
          const isActive = tab.to === activeTo;
          return (
            <Link
              key={tab.to}
              ref={isActive ? activeTabRef : undefined}
              to={tab.to}
              params={{ id: propertyId, propertyId }}
              // Der Listenkontext muss den Reiterwechsel überleben: ein Link ohne `search`
              // erzeugt bei TanStack ein leeres Suchobjekt, und weil weder `/properties` noch
              // `/properties/$id` ein `validateSearch` haben, holt es niemand zurück. Die
              // Pfeile aus F4 liefen danach durch eine andere Liste als die, aus der der
              // Nutzer kam.
              search={(prev) => prev}
              role="tab"
              aria-selected={isActive}
              className={cn(
                'shrink-0 rounded-full px-4 py-2 text-[13px] font-medium transition-colors',
                isActive ? 'bg-navy text-white' : 'text-ink-soft hover:bg-paper/60 hover:text-ink',
              )}
            >
              {tab.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
